const STORAGE_KEY = "nm_map_content";

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function createId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeWork(item = {}) {
  const year = String(item.year || "").trim();
  const organization = String(item.organization || "").trim();
  const service = String(item.service || "").trim();
  if (!year && !organization && !service) return null;

  return {
    id: String(item.id || createId()),
    year,
    organization,
    service,
    serviceUrl: String(item.serviceUrl || "").trim(),
  };
}

function normalizeRegion(item = {}) {
  const id = String(item.id || "").trim();
  const title = String(item.title || "").trim();
  if (!id || !title) return null;

  const works = Array.isArray(item.works)
    ? item.works.map(normalizeWork).filter(Boolean)
    : [];

  return { id, title, works };
}

function getDefaultData() {
  return { regions: [] };
}

function normalizeData(raw) {
  if (!raw || typeof raw !== "object") return getDefaultData();
  const regions = Array.isArray(raw.regions)
    ? raw.regions.map(normalizeRegion).filter(Boolean)
    : [];
  return { regions };
}

function getMapData() {
  const cached = window.NMApi?.cache?.map;
  if (cached) return normalizeData(cached);
  return getDefaultData();
}

function saveMapData(data) {
  const normalized = normalizeData(data);
  if (window.NMApi) {
    window.NMApi.cache.map = normalized;
    window.NMApi.save("map", normalized).catch((error) => {
      window.alert(error.message || "Не удалось сохранить карту"); // eslint-disable-line no-alert
    });
  }
  return normalized;
}

function getRegionById(id, data) {
  const source = data || getMapData();
  return source.regions.find((item) => item.id === id) || null;
}

function upsertRegion(region) {
  const data = getMapData();
  const normalized = normalizeRegion(region);
  if (!normalized) return data;

  const index = data.regions.findIndex((item) => item.id === normalized.id);
  if (index >= 0) {
    data.regions[index] = normalized;
  } else {
    data.regions.push(normalized);
  }
  return saveMapData(data);
}

function deleteRegion(id) {
  const data = getMapData();
  data.regions = data.regions.filter((item) => item.id !== id);
  return saveMapData(data);
}

function suppressMergedInteriorFragments(svg) {
  if (!svg) return;

  const merged = Array.from(svg.querySelectorAll('[data-merged-region="true"]'));
  if (!merged.length) return;

  Array.from(svg.querySelectorAll("path")).forEach((path) => {
    if (path.getAttribute("data-merged-region") === "true") {
      path.removeAttribute("data-covered-by-merge");
      return;
    }

    let covered = false;
    try {
      const box = path.getBBox();
      if (!box.width || !box.height) {
        path.removeAttribute("data-covered-by-merge");
        return;
      }

      const point = svg.createSVGPoint();
      const samples = [
        [0.5, 0.5],
        [0.35, 0.35],
        [0.65, 0.65],
        [0.35, 0.65],
        [0.65, 0.35],
      ];
      let hits = 0;
      samples.forEach(([fx, fy]) => {
        point.x = box.x + box.width * fx;
        point.y = box.y + box.height * fy;
        if (merged.some((item) => item.isPointInFill(point))) hits += 1;
      });
      covered = hits >= 3;
    } catch (error) {
      covered = false;
    }

    if (covered) path.setAttribute("data-covered-by-merge", "true");
    else path.removeAttribute("data-covered-by-merge");
  });
}

function prepareMapSvg(svg) {
  if (!svg) return [];
  const paths = Array.from(svg.querySelectorAll("path"));

  // Freeze source indices once. Moving merged paths to paint above neighbors
  // must not renumber region ids on later prepareMapSvg() calls.
  paths.forEach((path, index) => {
    if (!path.hasAttribute("data-region-index")) {
      path.setAttribute("data-region-index", String(index));
    }
    const stableIndex = path.getAttribute("data-region-index");
    path.setAttribute("data-region-id", `region-${stableIndex}`);
    path.classList.add("location-section__region");
  });

  paths
    .filter((path) => path.getAttribute("data-merged-region") === "true")
    .forEach((path) => {
      path.parentNode.appendChild(path);
    });

  suppressMergedInteriorFragments(svg);

  return paths;
}

function applyNamedRegions(svg, data) {
  if (!svg) return;
  const source = data || getMapData();
  const named = new Map(source.regions.map((item) => [item.id, item]));

  svg.querySelectorAll("[data-region-id]").forEach((path) => {
    const id = path.getAttribute("data-region-id");
    const region = named.get(id);
    path.classList.toggle("is-named", Boolean(region));
    if (region) {
      path.setAttribute("data-region-title", region.title);
      path.setAttribute("aria-label", region.title);
    } else {
      path.removeAttribute("data-region-title");
      path.removeAttribute("aria-label");
    }
  });
}

function getPathCenterPercent(path, svg) {
  if (!path || !svg) return null;

  try {
    const bbox = path.getBBox();
    const viewBox = svg.viewBox?.baseVal;
    const width = viewBox?.width || svg.clientWidth || 1680;
    const height = viewBox?.height || svg.clientHeight || 898;
    if (!width || !height) return null;

    return {
      left: ((bbox.x + bbox.width / 2) / width) * 100,
      top: ((bbox.y + bbox.height / 2) / height) * 100,
    };
  } catch (error) {
    return null;
  }
}

function groupWorksByYear(works) {
  const groups = {};
  (works || []).forEach((work) => {
    const year = work.year || "—";
    if (!groups[year]) groups[year] = [];
    groups[year].push(work);
  });

  return Object.keys(groups)
    .sort((a, b) => String(b).localeCompare(String(a), "ru", { numeric: true }))
    .map((year) => ({ year, works: groups[year] }));
}

function renderServiceLabel(work) {
  const label = escapeHtml(work.service || "Услуга");
  if (work.serviceUrl) {
    return `<a href="${escapeHtml(work.serviceUrl)}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  }
  return `<p>${label}</p>`;
}

function fillLocationPopup(region) {
  const popup = document.getElementById("popup-location");
  if (!popup || !region) return;

  const title = popup.querySelector(".popup__location-title");
  const timeline = popup.querySelector(".popup__location-timeline");

  if (title) title.textContent = region.title || "Регион";

  if (!timeline) return;

  const groups = groupWorksByYear(region.works);
  if (!groups.length) {
    timeline.innerHTML = `
      <div class="popup__location-block">
        <div class="popup__location-project d2">
          <p>История работ пока не добавлена</p>
        </div>
      </div>
    `;
    return;
  }

  timeline.innerHTML = groups.map((group) => `
    <div class="popup__location-block">
      <span class="popup__location-year h5">${escapeHtml(group.year)}</span>
      ${group.works.map((work) => `
        <div class="popup__location-project d2">
          <p>${escapeHtml(work.organization || "Организация")}</p>
          ${renderServiceLabel(work)}
        </div>
      `).join("")}
    </div>
  `).join("");
}

const STATIC_MAP_REGION_ID = "region-279";

function openRegionPopup(regionId) {
  if (!regionId || regionId === STATIC_MAP_REGION_ID) return;
  const region = getRegionById(regionId);
  if (!region) return;
  fillLocationPopup(region);
  if (typeof window.openPopup === "function") {
    window.openPopup("popup-location");
  }
}

function initMarkPoppers(marksRoot, { force = false } = {}) {
  // Public map uses CSS-positioned labels; Popper only for admin force mode.
  if (!marksRoot || !force) return;

  // eslint-disable-next-line global-require, import/no-extraneous-dependencies
  const { createPopper } = require("@popperjs/core");

  marksRoot.querySelectorAll(".location-section__mark").forEach((mark) => {
    const tooltip = mark.querySelector(".location-section__city");
    const button = mark.querySelector(".location-section__dot");
    if (!tooltip || !button) return;

    createPopper(button, tooltip, {
      strategy: "absolute",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
        {
          name: "preventOverflow",
          options: {
            boundary: marksRoot,
            padding: 8,
          },
        },
      ],
    });
  });
}

function renderLocationMarks(root, data, { forcePoppers = false } = {}) {
  const marks = root?.querySelector("[data-location-marks]");
  const svg = root?.querySelector(".location-section__map");
  if (!marks) return;

  const { regions } = data || getMapData();
  if (!regions.length) {
    marks.innerHTML = "";
    return;
  }

  marks.innerHTML = regions.map((region) => {
    const path = svg?.querySelector(`[data-region-id="${region.id}"]`);
    const center = getPathCenterPercent(path, svg);
    const top = center ? center.top.toFixed(2) : 50;
    const left = center ? center.left.toFixed(2) : 50;
    const id = escapeHtml(region.id);
    const title = escapeHtml(region.title);
    const isStatic = region.id === STATIC_MAP_REGION_ID;
    const radius = isStatic ? "8px" : "6px";
    const openAttr = isStatic ? "" : ` data-location-open="${id}"`;
    const dotRole = isStatic
      ? `aria-hidden="true"`
      : `role="button" tabindex="0" aria-label="${title}" title="${title}"`;
    const cityTag = isStatic
      ? `<span class="location-section__city">${title}</span>`
      : `<button class="location-section__city" type="button" data-location-open="${id}">${title}</button>`;

    return `
      <div class="location-section__mark" style="top: ${top}%; left: ${left}%">
        <div class="location-section__dot"${openAttr} ${dotRole} data-location-id="${id}">
          <svg height="24px" width="24px" aria-hidden="true">
            <circle cx="50%" cy="50%" r="${radius}"></circle>
            <circle class="location-section__dot-pulse" cx="50%" cy="50%" r="${radius}"></circle>
          </svg>
        </div>
        ${cityTag}
      </div>
    `;
  }).join("");

  initMarkPoppers(marks, { force: forcePoppers });
}

function syncCitiesMore(section) {
  const moreBtn = section.querySelector("[data-location-cities-more]");
  if (!moreBtn) return;
  const count = section.querySelectorAll(".location-section__mark").length;
  const node = moreBtn;
  node.hidden = count <= 6;
  if (count <= 6) section.classList.remove("is-cities-expanded");
  node.textContent = section.classList.contains("is-cities-expanded")
    ? "Скрыть"
    : "Показать ещё";
}

function bindPublicMap(section) {
  if (!section || section.dataset.mapBound === "true") return;
  const root = section;
  root.dataset.mapBound = "true";

  root.addEventListener("click", (event) => {
    const moreBtn = event.target.closest("[data-location-cities-more]");
    if (moreBtn) {
      event.preventDefault();
      root.classList.toggle("is-cities-expanded");
      syncCitiesMore(root);
      return;
    }

    const trigger = event.target.closest("[data-location-open]");
    if (!trigger) return;

    event.preventDefault();
    openRegionPopup(trigger.getAttribute("data-location-open"));
  });
}

function hydratePublicMaps() {
  const sections = document.querySelectorAll(".location-section");
  if (!sections.length) return;

  const data = getMapData();
  sections.forEach((section) => {
    const svg = section.querySelector(".location-section__map");
    prepareMapSvg(svg);
    applyNamedRegions(svg, data);
    renderLocationMarks(section, data);
    bindPublicMap(section);
    syncCitiesMore(section);
  });
}

window.NMMapContent = {
  STORAGE_KEY,
  escapeHtml,
  createId,
  getMapData,
  saveMapData,
  getDefaultData,
  normalizeRegion,
  normalizeWork,
  getRegionById,
  upsertRegion,
  deleteRegion,
  prepareMapSvg,
  applyNamedRegions,
  fillLocationPopup,
  hydratePublicMaps,
  renderLocationMarks,
  getPathCenterPercent,
  groupWorksByYear,
};

window.NMApi?.ready?.then(hydratePublicMaps);
